import { MicHandler, MicOptions } from "../../../models/microphone";
import { AbstMic } from "../common";
import {
  encodeRawData,
  concatenateFloat32Array,
  buildWavHeader
} from "../../audio";
import { ScriptNodeManager, getMediaStreamError } from "../input";
import RecordRTC from "recordrtc";
import { MIC_ERR_LIFECYCLE } from "../../../constants/microphone";

export interface MicScriptNodeOptions extends MicOptions {
  /**
   * Custom AudioContext object
   */
  audioCtx: AudioContext;
}

function concatenate(resultConstructor: any, ...arrays: any) {
  let totalLength = 0;
  for (const arr of arrays) {
    totalLength += arr.length;
  }
  const result = new resultConstructor(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

function convertPcmToWav(buffer: any) {
  // create WAV header
  const sampleRate = 16000;
  const numChannels = 1;
  const [type, format] = [Uint8Array, 1];
  const wavHeader = new Uint8Array(
    buildWavHeader({
      numFrames: buffer.byteLength,
      bytesPerSample: 2,
      sampleRate: sampleRate,
      numChannels: numChannels,
      format: format
    })
  );

  // wavHeader + raw PCM Bytes
  const wavBytes = new Uint8Array(wavHeader.length + buffer.byteLength);
  wavBytes.set(wavHeader, 0);
  wavBytes.set(new Uint8Array(buffer), wavHeader.length);
  return wavBytes;
}

function mergeLeftRightBuffers(config: any, callback?: any) {
  function mergeBuffers(channelBuffer: any, rLength: any) {
    const result = new Float64Array(rLength);
    let offset = 0;
    const lng = channelBuffer.length;

    for (let i = 0; i < lng; i++) {
      const buffer = channelBuffer[i];
      result.set(buffer, offset);
      offset += buffer.length;
    }

    return result;
  }

  function linearInterpolate(before: any, after: any, atPoint: any) {
    return before + (after - before) * atPoint;
  }

  // for changing the sampling rate, reference:
  // http://stackoverflow.com/a/28977136/552182
  function interpolateArray(data: any, newSampleRate: any, oldSampleRate: any) {
    const fitCount = Math.round(data.length * (newSampleRate / oldSampleRate));
    //var newData = new Array();
    const newData = [];
    //var springFactor = new Number((data.length - 1) / (fitCount - 1));
    const springFactor = Number((data.length - 1) / (fitCount - 1));
    newData[0] = data[0]; // for new allocation
    for (let i = 1; i < fitCount - 1; i++) {
      const tmp = i * springFactor;
      const before = Number(Number(Math.floor(tmp)).toFixed());
      const after = Number(Number(Math.ceil(tmp)).toFixed());
      const atPoint = tmp - before;
      newData[i] = linearInterpolate(data[before], data[after], atPoint);
    }
    newData[fitCount - 1] = data[data.length - 1]; // for new allocation
    return newData;
  }

  function mergeAudioBuffers(config: any, cb?: any) {
    const numberOfAudioChannels = config.numberOfAudioChannels;

    // todo: "slice(0)" --- is it causes loop? Should be removed?
    let leftBuffers = config.leftBuffers.slice(0);
    let rightBuffers = config.rightBuffers.slice(0);
    let sampleRate = config.sampleRate;
    const desiredSampRate = 16000;
    const internalInterleavedLength = config.internalInterleavedLength;

    if (numberOfAudioChannels === 2) {
      leftBuffers = mergeBuffers(leftBuffers, internalInterleavedLength);
      rightBuffers = mergeBuffers(rightBuffers, internalInterleavedLength);
      if (desiredSampRate) {
        leftBuffers = interpolateArray(
          leftBuffers,
          desiredSampRate,
          sampleRate
        );
        rightBuffers = interpolateArray(
          rightBuffers,
          desiredSampRate,
          sampleRate
        );
      }
    }

    if (numberOfAudioChannels === 1) {
      leftBuffers = mergeBuffers(leftBuffers, internalInterleavedLength);
      if (desiredSampRate) {
        leftBuffers = interpolateArray(
          leftBuffers,
          desiredSampRate,
          sampleRate
        );
      }
    }

    // set sample rate as desired sample rate
    if (desiredSampRate) {
      sampleRate = desiredSampRate;
    }

    function interleave(leftChannel: any, rightChannel: any) {
      const length = leftChannel.length + rightChannel.length;

      const result = new Float64Array(length);

      let inputIndex = 0;

      for (let index = 0; index < length; ) {
        result[index++] = leftChannel[inputIndex];
        result[index++] = rightChannel[inputIndex];
        inputIndex++;
      }
      return result;
    }

    function writeUTFBytes(view: any, offset: any, string: any) {
      const lng = string.length;
      for (let i = 0; i < lng; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    }

    // interleave both channels together
    let interleaved;

    if (numberOfAudioChannels === 2) {
      interleaved = interleave(leftBuffers, rightBuffers);
    }

    if (numberOfAudioChannels === 1) {
      interleaved = leftBuffers;
    }

    const interleavedLength = interleaved.length;

    // create wav file
    const resultingBufferLength = 44 + interleavedLength * 2;

    const buffer = new ArrayBuffer(resultingBufferLength);

    const view = new DataView(buffer);

    // RIFF chunk descriptor/identifier
    writeUTFBytes(view, 0, "RIFF");

    // RIFF chunk length
    view.setUint32(4, 44 + interleavedLength * 2, true);

    // RIFF type
    writeUTFBytes(view, 8, "WAVE");

    // format chunk identifier
    // FMT sub-chunk
    writeUTFBytes(view, 12, "fmt ");

    // format chunk length
    view.setUint32(16, 16, true);

    // sample format (raw)
    view.setUint16(20, 1, true);

    // stereo (2 channels)
    view.setUint16(22, numberOfAudioChannels, true);

    // sample rate
    view.setUint32(24, sampleRate, true);

    // byte rate (sample rate * block align)
    view.setUint32(28, sampleRate * 2, true);

    // block align (channel count * bytes per sample)
    view.setUint16(32, numberOfAudioChannels * 2, true);

    // bits per sample
    view.setUint16(34, 16, true);

    // data sub-chunk
    // data chunk identifier
    writeUTFBytes(view, 36, "data");

    // data chunk length
    view.setUint32(40, interleavedLength * 2, true);

    // write the PCM samples
    const lng = interleavedLength;
    let index = 44;
    const volume = 1;
    for (let i = 0; i < lng; i++) {
      view.setInt16(index, interleaved[i] * (0x7fff * volume), true);
      index += 2;
    }

    if (cb) {
      return cb({
        buffer: buffer,
        view: view
      });
    } else {
      return {
        buffer,
        view
      };
    }
  }

  if (callback) {
    mergeAudioBuffers(config, function(data: any) {
      callback(data.buffer, data.view);
    });
  } else {
    return mergeAudioBuffers(config);
  }
}

/**
 */
export class MicScriptNodeRecorder extends AbstMic implements MicHandler {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  private _audioContext: AudioContext;
  private _recorderManager: ScriptNodeManager;

  /**
   * Raw data taken from the ScriptNode. Only mono channel is recorded
   */
  private _totalBuffer: Float32Array = new Float32Array(0);

  private _totalRawBuffer: Uint8Array = new Uint8Array(0);
  /**
   * Is window.RawAudioRecorder available
   */
  readonly _rawAudioRecorderAvailable: boolean;

  private _recordRTCRecorder?: RecordRTC;

  readonly _isChrome: boolean;

  private _microphone?: MediaStream = undefined;

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: MicScriptNodeOptions) {
    super(options);

    this._rawAudioRecorderAvailable = (window as any).RawAudioRecorder;

    this._recorderManager = new ScriptNodeManager({
      log: msg => this.log(msg)
    });

    const appVersion = navigator.appVersion;
    this._isChrome = appVersion.match(/chrome/i) !== null;

    this._audioContext = options.audioCtx;
  }

  async init() {
    this._status = "ready";
  }

  async createRTCRecorder() {
    try {
      const microphone = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      this._recordRTCRecorder = new RecordRTC(microphone, {
        numberOfAudioChannels: 1,
        recorderType: RecordRTC.StereoAudioRecorder,
        type: "audio"
      });
    } catch (error) {
      console.log("createRTCRecorder error:", error);
    }
  }

  // --------------------------------------------------------------------------
  //    Methods: Microphone
  // --------------------------------------------------------------------------
  async _captureMicrophone() {
    try {
      const microphone = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      this._recordRTCRecorder = new RecordRTC(microphone, {
        disableLogs: false,
        numberOfAudioChannels: 1,
        recorderType: RecordRTC.StereoAudioRecorder,
        type: "audio"
      });

      this._microphone = microphone;
    } catch (error) {
      console.log("createRTCRecorder error:", error);
    }
  }

  _releaseMicrophone() {
    const tracks = this._microphone?.getTracks();

    tracks?.forEach((track: any) => {
      track.stop();
    });

    (this._microphone as any)?.stop();
  }

  async start(): Promise<void> {
    if (this._rawAudioRecorderAvailable) {
      await this.startWithRawAudioRecorder();
    } else if (this._isChrome) {
      await this.startWithRecordRTC();
    } else {
      await this.startWithRecorderManager();
    }
  }

  async startWithRecordRTC() {
    await this._resetRawValues();

    try {
      await this._captureMicrophone();

      this._recordRTCRecorder?.reset();

      this._recordRTCRecorder?.startRecording();

      this._status = "recording";
    } catch (error) {
      const mediaStreamErr = getMediaStreamError(error);
      if (mediaStreamErr) {
        this.addError(mediaStreamErr);
        this._status = "init-err";
      } else {
        this.addUnknownError({
          location: "start() method fails",
          error,
          errAsStringify: JSON.stringify(error)
        });
        this._status = "ready";
      }
    }

    this._onRecordingStarted();
  }

  async startWithRecorderManager(): Promise<void> {
    this.log("START");
    this._resetValues();

    try {
      this._status = "permission";

      await this._recorderManager.start(ev => this.onNewData(ev));
      this.log("STARTED all connected");

      this._status = "recording";
      this._timeStart = window.performance.now();
    } catch (err) {
      const mediaStreamErr = getMediaStreamError(err);
      if (mediaStreamErr) {
        this.addError(mediaStreamErr);
        this._status = "init-err";
      } else {
        this.addUnknownError({
          location: "start() method fails",
          err,
          errAsStringify: JSON.stringify(err)
        });
        this._status = "ready";
      }
    }

    this._onRecordingStarted();
  }

  async rawAudioRecorderOnStart() {
    this.log("START");
    await this._resetRawValues();

    try {
      this._status = "permission";
      this._status = "recording";

      this._timeStart = window.performance.now();
    } catch (err) {
      const mediaStreamErr = getMediaStreamError(err);
      if (mediaStreamErr) {
        this.addError(mediaStreamErr);
        this._status = "init-err";
      } else {
        this.addUnknownError({
          location: "start() method fails",
          err,
          errAsStringify: JSON.stringify(err)
        });
        this._status = "ready";
      }
    }

    this._onRecordingStarted();
  }

  rawAudioRecorderOnNewData(newData: number[]) {
    this.onNewRawData(newData);
  }

  async rawAudioRecorderOnStop() {
    this.log("STOP");

    const audio = this.buildRawAudio();
    this.addNewData(
      { blob: audio },
      {
        DisplayText: "",
        IntermediateResults: [],
        Lexical: "",
        MaskedDisplayText: "",
        MaskedLexical: ""
      }
    );

    this._onRecordingStopped([audio]);
    this._timeEnd = window.performance.now();

    this._status = "stopped";
  }

  rawAudioRecorderOnError(error: any) {
    console.log("raw audio recorder error:", error);
  }

  async startWithRawAudioRecorder(): Promise<void> {
    const rawAudioRecorder = (window as any).RawAudioRecorder;

    rawAudioRecorder.startListening(
      () => this.rawAudioRecorderOnStart(),
      (newData: number[]) => this.rawAudioRecorderOnNewData(newData),
      () => this.rawAudioRecorderOnStop(),
      (error: any) => this.rawAudioRecorderOnError(error)
    );
  }

  /**
   * Stop recording
   */
  async stop(): Promise<void> {
    if (this._rawAudioRecorderAvailable) {
      await this.stopWithRawAudioRecorder();
    } else if (this._isChrome) {
      await this.stopWithRecordRTC();
    } else {
      await this.stopWithRecorderManager();
    }
  }

  async stopWithRecorderManager() {
    this.log("STOP");

    const audio = this.buildAudio();
    this.addNewData(
      { blob: audio },
      {
        DisplayText: "",
        IntermediateResults: [],
        Lexical: "",
        MaskedDisplayText: "",
        MaskedLexical: ""
      }
    );

    this._onRecordingStopped([audio]);
    this._timeEnd = window.performance.now();

    // Stop the recording
    try {
      await this._recorderManager.stop();
    } catch (err) {
      console.warn("Error when stopping MicScriptNodeRecorder:", err);
      this.addUnknownError({ err, errAsStringify: JSON.stringify(err) });
    }

    this._status = "stopped";
  }

  async stopWithRecordRTC() {
    this._recordRTCRecorder?.stopRecording(() => this.processRecordRTCData());

    this._releaseMicrophone();
  }

  async stopWithRawAudioRecorder() {
    const rawAudioRecorder = (window as any).RawAudioRecorder;

    this._isProcessing = true;
    rawAudioRecorder.stopListening();
  }

  async processRecordRTCData() {
    const internalRecorder: any = this._recordRTCRecorder?.getInternalRecorder();

    if (internalRecorder) {
      const leftChannel = internalRecorder.leftchannel;
      const rightChannel = internalRecorder.rightchannel;

      try {
        const { buffer, view } = mergeLeftRightBuffers({
          desiredSampRate: 16000,
          sampleRate: internalRecorder.sampleRate,
          numberOfAudioChannels: internalRecorder.numberOfAudioChannels,
          internalInterleavedLength: internalRecorder.recordingLength,
          leftBuffers: leftChannel,
          rightBuffers:
            internalRecorder.numberOfAudioChannels === 1 ? [] : rightChannel
        });

        // ------------------------------------------------------------
        // here is your own WAV (generated by your own codes)
        // ------------------------------------------------------------
        const blob = new Blob([buffer], {
          type: "audio/wav"
        });

        const decodedBuffer = await new Response(blob).arrayBuffer();

        const decodedData = await this._audioContext.decodeAudioData(
          decodedBuffer
        );

        this._onRecordingStopped([blob]);

        this._status = "stopped";
      } catch (error) {
        console.log({ error });
      }
    }
  }

  /**
   * Force stop a in-progress processing
   */
  async cancel(): Promise<void> {
    this._onRecordingError("Cancelled");
    this._onRecognizingError("Cancelled");

    this._status = "stopped";
    this._isProcessing = false;
  }

  /**
   * Reset all reset-able values
   */
  private async _resetValues() {
    this._error = undefined;
    this._totalBuffer = new Float32Array(0);
  }

  private async _resetRawValues() {
    this._error = undefined;
    this._totalRawBuffer = new Uint8Array(0);
  }

  // --------------------------------------------------------------------------
  //    Methods: ScriptProcessorNode
  // --------------------------------------------------------------------------
  onNewData(ev: AudioProcessingEvent) {
    const newFrames = ev.inputBuffer.getChannelData(0);

    if (this._totalBuffer.length === 32000) {
      const silenceDetected = this._totalBuffer.every(value => {
        return value === 0;
      });

      if (silenceDetected) {
        this.onSilenceDetected();
        return;
      }
    }

    this._totalBuffer = concatenateFloat32Array(this._totalBuffer, newFrames);
  }

  onNewRawData(chunks: number[]) {
    const newFrames = Uint8Array.from(chunks);

    if (this._totalBuffer.length === 32000) {
      const silenceDetected = this._totalRawBuffer.every(value => {
        return value === 0;
      });

      if (silenceDetected) {
        this.onSilenceDetected();
        return;
      }
    }

    this._totalRawBuffer = concatenate(
      Uint8Array,
      this._totalRawBuffer,
      newFrames
    );
  }

  async onSilenceDetected(): Promise<void> {
    this.stop();
    this._onSilenceDetected();
    await this.cancel();
    this._resetValues();
    this._resetRawValues();
    this._status = "ready";
  }

  buildAudio(): Blob {
    const wavData = encodeRawData(
      this._totalBuffer,
      this._recorderManager.audioContextSampleRate,
      this._azSampleRate,
      true
    );

    // Adding a type has no effect on blob.size
    const wavBlob = new Blob([wavData], { type: this._azAudioType });
    return wavBlob;
  }

  buildRawAudio(): Blob {
    const wavData = convertPcmToWav(this._totalRawBuffer);

    const wavBlob = new Blob([wavData], { type: this._azAudioType });
    return wavBlob;
  }
}
